import { Box, Grid, StyleProps, Tooltip } from '@chakra-ui/react';
import { format, isSameMonth, isWithinInterval } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@bonusly/ds-typography';
import { rem } from '@bonusly/ds-utils';

import { DateRange } from '../../types';
import { useMonthPickerContext } from '../month-picker-context';

export type MonthPickerProps = StyleProps;

type MonthProps = {
	month: Date;
	currentYear: number;
	onHoverMonth: (month: Date) => void;
	onSelectMonth: (month: Date) => void;
	onClearHover: () => void;
	selectionEnd: Date | null;
	selectionStart: Date | null;
	disableMonthIf?: (month: Date, interimValue: DateRange) => boolean;
	hasMultiMonthSelection: boolean | null;
};

export const MonthPicker: FC<MonthPickerProps> = (props) => {
	const { currentYear, onHoverMonth, onSelectMonth, selectionEnd, selectionStart, disableMonthIf, onClearHover } = useMonthPickerContext();

	// Generate array of months for the current year
	const months = Array.from({ length: 12 }, (_, index) => {
		return new Date(currentYear, index, 1);
	});

	// Check if we have a multi-month selection to determine gap styling
	const hasMultiMonthSelection = selectionStart && selectionEnd && selectionStart.getMonth() !== selectionEnd.getMonth();

	return (
		<Grid maxW={rem(320)} rowGap={2} columnGap={0} templateColumns="repeat(3, 1fr)" {...props}>
			{months.map((month) => {
				return (
					<Month
						key={format(month, 'M-y')}
						month={month}
						currentYear={currentYear}
						onHoverMonth={onHoverMonth}
						onSelectMonth={onSelectMonth}
						onClearHover={onClearHover}
						selectionEnd={selectionEnd}
						selectionStart={selectionStart}
						disableMonthIf={disableMonthIf}
						hasMultiMonthSelection={hasMultiMonthSelection}
					/>
				);
			})}
		</Grid>
	);
};

const Month: FC<MonthProps> = ({ month, onHoverMonth, onSelectMonth, onClearHover, selectionEnd, selectionStart, disableMonthIf, hasMultiMonthSelection }) => {
	const { t } = useTranslation('components');
	const { interimValue, disabledMonthTooltipText } = useMonthPickerContext();
	const isSelectedStart = selectionStart && isSameMonth(month, selectionStart);
	const isSelectedEnd = selectionEnd && isSameMonth(month, selectionEnd);
	const isSelected = isSelectedStart || isSelectedEnd;
	const isDisabled = disableMonthIf?.(month, interimValue) ?? false;

	const isWithinSelection = selectionStart && selectionEnd && isWithinInterval(month, { start: selectionStart, end: selectionEnd });

	// Determine if this month is at the start or end of a row in the 3-column grid
	const monthIndex = month.getMonth();
	const isStartOfRow = monthIndex % 3 === 0; // Months 0, 3, 6, 9 (Jan, Apr, Jul, Oct)
	const isEndOfRow = monthIndex % 3 === 2; // Months 2, 5, 8, 11 (Mar, Jun, Sep, Dec)

	let textColor = 'text.default';

	if (isSelected) {
		textColor = 'secondary.fgOnSurface';
	}

	if (isDisabled) {
		textColor = 'text.disabled';
	}

	const handleClick = () => {
		if (!isDisabled) {
			onSelectMonth(month);
		}
	};

	const handleHover = () => {
		if (!isDisabled) {
			onHoverMonth(month);
		}
	};

	const monthButton = (
		<Box
			alignItems="center"
			aria-label={t('date_picker.month_value', { date: month })}
			as="button"
			backgroundColor={isSelected ? 'secondary.bg' : 'transparent'}
			borderRadius="md"
			color={textColor}
			cursor={isDisabled ? 'not-allowed' : 'pointer'}
			display="flex"
			justifyContent="center"
			minH={rem(48)}
			w="100%"
			h="100%"
			onClick={handleClick}
			disabled={isDisabled}
			onMouseEnter={handleHover}
			onMouseLeave={onClearHover}
			px={3}
			py={2}
			position="relative"
			transitionDuration="fast"
			transitionProperty="background-color, color"
			type="button"
			_focus={{
				backgroundColor: isDisabled ? 'transparent' : isSelected ? 'secondary.bg' : 'secondary.bgAlpha',
			}}
			_hover={{
				backgroundColor: isDisabled ? 'transparent' : isSelected ? 'secondary.bg' : 'secondary.bgAlpha',
			}}
			_active={{
				backgroundColor: isDisabled ? 'transparent' : 'secondary.bgActive',
			}}
			{...(isSelected && {
				'data-selected': true,
			})}
		>
			<Text fontSize="sm" fontWeight={isSelected ? 'semibold' : 'medium'}>
				{format(month, 'MMM')}
			</Text>
		</Box>
	);

	return (
		<Box
			position="relative"
			minH={rem(48)}
			{...((isWithinSelection || isSelected) &&
				hasMultiMonthSelection && {
					backgroundColor: 'secondary.bgAlpha',
					// Apply border radius to selected start/end dates OR row edges within range
					...((isSelectedStart || (isStartOfRow && (isWithinSelection || isSelected))) && {
						borderTopLeftRadius: 'md',
						borderBottomLeftRadius: 'md',
					}),
					...((isSelectedEnd || (isEndOfRow && (isWithinSelection || isSelected))) && {
						borderTopRightRadius: 'md',
						borderBottomRightRadius: 'md',
					}),
				})}
		>
			{isDisabled && disabledMonthTooltipText ? (
				<Tooltip label={disabledMonthTooltipText} placement="top">
					{monthButton}
				</Tooltip>
			) : (
				monthButton
			)}
		</Box>
	);
};
